'use client';
import React, { useEffect, useState, useCallback } from 'react';
import { TrendingUp, TrendingDown, Loader2 } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import { getTransactionSummary } from '@/lib/transactions';

const BUSINESSES = ['Park-N-Lot', 'Cree8tiveminds', 'Cre8Loads'];
const BUSINESS_EMOJIS: Record<string, string> = {
  'Park-N-Lot': '🚗',
  'Cree8tiveminds': '🎨',
  'Cre8Loads': '🚛',
};

export default function BusinessHealthCards() {
  const { user } = useAuth();
  const [bizData, setBizData] = useState<Record<string, { income: number; expense: number }>>({});
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    try {
      const summary = await getTransactionSummary(user.id);
      setBizData(summary.byBusiness);
    } catch {
      setBizData({});
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-8 flex items-center justify-center">
        <Loader2 size={20} className="text-accent animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-border">
        <h3 className="text-base font-600 text-foreground">Business Health</h3>
        <p className="text-xs text-muted-foreground mt-0.5">P&L across all three businesses</p>
      </div>
      <div className="divide-y divide-border/50">
        {BUSINESSES.map((biz) => {
          const data = bizData[biz] || { income: 0, expense: 0 };
          const profit = data.income - data.expense;
          const margin = data.income > 0 ? ((profit / data.income) * 100) : 0;
          const isPositive = profit >= 0;

          return (
            <div key={biz} className="px-5 py-4 hover:bg-muted/20 transition-colors">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{BUSINESS_EMOJIS[biz]}</span>
                  <div>
                    <p className="text-sm font-600 text-foreground">{biz}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {data.income > 0 || data.expense > 0 ? 'Active' : 'No transactions yet'}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-base font-700 font-mono-nums ${isPositive ? 'text-success' : 'text-danger'}`}>
                    {isPositive ? '+' : ''}${profit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  <div className={`flex items-center gap-0.5 justify-end text-xs font-600 ${isPositive ? 'text-success' : 'text-danger'}`}>
                    {isPositive ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                    {Math.abs(margin).toFixed(1)}% margin
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="bg-muted/40 rounded-lg px-3 py-2">
                  <p className="text-[10px] text-muted-foreground mb-0.5">Revenue</p>
                  <p className="text-sm font-700 font-mono-nums text-success">
                    +${data.income.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="bg-muted/40 rounded-lg px-3 py-2">
                  <p className="text-[10px] text-muted-foreground mb-0.5">Expenses</p>
                  <p className="text-sm font-700 font-mono-nums text-foreground">
                    ${data.expense.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              </div>

              {data.income > 0 && (
                <div className="progress-bar-bg h-1.5">
                  <div
                    className={`h-full rounded-full transition-all duration-700 ${isPositive ? 'bg-success' : 'bg-danger'}`}
                    style={{ width: `${Math.min(Math.abs(margin), 100)}%` }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}